import { GAME_CONFIG } from '../config/constants';
import { GameService } from '../services/gameService';
import { DateService } from '../services/dateService';
import { MENTORS } from '../data/mentors';

export const initialGameState = {
  version: 2.1,
  seeds: 100,
  sps: 750, // Corrected initial SPS to match mentors.js
  planters: 0,
  gardeners: 0,
  effLevel: 1,
  autoLevel: 1,
  compost: 1.0, // Renamed from prestigeMultiplier for consistency
  prestigeLevel: 0,
  ownedCharacters: ['butler'],
  mentorLevels: {},
  lastTick: Date.now(),
  history: new Array(40).fill(0),
  profile: {
    name: "Gardener"
  },
  focus: {
    status: 'idle',
    length: 25,
    remaining: 0,
    scene: 'dawn',
    artSeed: 12345
  },
  gallery: [],
  streak: {
    current: 0,
    best: 0,
    lastDay: ""
  },
  daily: {
    rewards: GAME_CONFIG.dailyRewards,
    index: 0,
    lastClaimDay: "",
    completedToday: false
  },
  dailyWater: {
    lastDay: "",
    used: false
  },
  lastWateringReward: 0,
  plots: [
    { status: 'ready', minutes: 5, endAt: 0, reward: 0, cost: 0, startedAt: 0 },
    { status: 'ready', minutes: 15, endAt: 0, reward: 0, cost: 0, startedAt: 0 },
    { status: 'ready', minutes: 30, endAt: 0, reward: 0, cost: 0, startedAt: 0 }
  ]
};

const recalculateTotalMentorSps = (ownedCharacters, mentorLevels) => {
  return GameService.calculateTotalMentorSps(ownedCharacters, mentorLevels);
};

export function gameReducer(state, action) {
  console.log('🔄 Reducer processing:', action.type, 'Current seeds:', state.seeds);
  let newState;

  switch (action.type) {
    case 'LOAD_STATE':
      const loadedState = {
        ...initialGameState,
        ...action.payload,
        profile: { ...initialGameState.profile, ...(action.payload?.profile || {}) },
        focus: { ...initialGameState.focus, ...(action.payload?.focus || {}) },
        streak: { ...initialGameState.streak, ...(action.payload?.streak || {}) },
        daily: { ...initialGameState.daily, ...(action.payload?.daily || {}) },
        dailyWater: { ...initialGameState.dailyWater, ...(action.payload?.dailyWater || {}) },
        plots: action.payload?.plots || initialGameState.plots,
        mentorLevels: action.payload?.mentorLevels || {},
        gallery: action.payload?.gallery || [],
      };

      // Recalculate compost multiplier to correct any legacy inconsistencies.
      // This ensures the multiplier always matches the prestige level based on current config.
      const correctCompost = Math.min(
        1.0 + ((loadedState.prestigeLevel || 0) * GAME_CONFIG.prestige.compostBonus),
        GAME_CONFIG.prestige.maxMultiplier
      );
      loadedState.compost = correctCompost;

      loadedState.sps = recalculateTotalMentorSps(loadedState.ownedCharacters, loadedState.mentorLevels);
      console.log('✅ Final loaded state seeds:', loadedState.seeds, 'Recalculated SPS:', loadedState.sps, 'Corrected Compost:', loadedState.compost);
      return loadedState;

    case 'TICK': {
      const now = Date.now();
      const deltaMs = Math.min(now - state.lastTick, 5000);
      const deltaSec = deltaMs / 1000;
      const sps = GameService.calculateSPS(state.planters, state.gardeners, state.autoLevel, state.sps, state.compost);
      const seedIncrease = sps * deltaSec;
      const newSeeds = state.seeds + seedIncrease;
      const newHistory = [...state.history];
      newHistory.shift();
      newHistory.push(sps);
      const today = DateService.getDayKey();
      let newDailyWater = { ...state.dailyWater };
      if (state.dailyWater.lastDay !== today) {
        newDailyWater = { lastDay: today, used: false };
      }
      newState = { ...state, seeds: newSeeds, lastTick: now, history: newHistory, dailyWater: newDailyWater };
      break;
    }

    case 'HARVEST': {
      const clickYield = GameService.calculateClickYield(state.effLevel, state.compost);
      newState = { ...state, seeds: state.seeds + clickYield };
      break;
    }

    case 'BUY_PLANTER': {
      const cost = GameService.calculatePlanterCost(state.planters);
      if (state.seeds >= cost) {
        newState = { ...state, seeds: state.seeds - cost, planters: state.planters + 1 };
      } else {
        newState = state;
      }
      break;
    }

    case 'BUY_GARDENER': {
      const cost = GameService.calculateGardenerCost(state.gardeners);
      if (state.seeds >= cost) {
        newState = { ...state, seeds: state.seeds - cost, gardeners: state.gardeners + 1 };
      } else {
        newState = state;
      }
      break;
    }

    case 'BUY_EFFICIENCY': {
      const cost = GameService.calculateEfficiencyCost(state.effLevel);
      if (state.seeds >= cost) {
        newState = { ...state, seeds: state.seeds - cost, effLevel: state.effLevel + 1 };
      } else {
        newState = state;
      }
      break;
    }

    case 'BUY_AUTOMATION': {
      const cost = GameService.calculateAutomationCost(state.autoLevel);
      if (state.seeds >= cost) {
        newState = { ...state, seeds: state.seeds - cost, autoLevel: state.autoLevel + 1 };
      } else {
        newState = state;
      }
      break;
    }

    case 'PRESTIGE': {
      const requirement = GameService.calculatePrestigeRequirement(state.prestigeLevel);
      if (state.seeds >= requirement) {
        newState = {
          ...state,
          seeds: 0,
          planters: 0,
          gardeners: 0,
          effLevel: 1,
          autoLevel: 1,
          lastTick: Date.now(),
          history: new Array(40).fill(0),
          prestigeLevel: state.prestigeLevel + 1,
          compost: Math.min(
            state.compost + GAME_CONFIG.prestige.compostBonus,
            GAME_CONFIG.prestige.maxMultiplier
          ),
        };
        console.log('✅ Prestige complete. New level:', newState.prestigeLevel);
      } else {
        newState = state;
      }
      break;
    }

    case 'UNLOCK_MENTOR': {
      const { mentor, cost } = action.payload;
      if (state.seeds >= cost && !state.ownedCharacters.includes(mentor.key)) {
        const newOwnedCharacters = [...state.ownedCharacters, mentor.key];
        const newSps = recalculateTotalMentorSps(newOwnedCharacters, state.mentorLevels);
        newState = {
          ...state,
          seeds: state.seeds - cost,
          ownedCharacters: newOwnedCharacters,
          sps: newSps,
        };
        console.log(`🔓 Unlocked ${mentor.name}. New total mentor SPS:`, newState.sps);
      } else {
        newState = state;
      }
      break;
    }

    case 'UPGRADE_MENTOR': {
      const { mentorKey } = action.payload;
      const mentor = MENTORS[mentorKey];
      if (!mentor || !state.ownedCharacters.includes(mentorKey) || !mentor.upgradeBaseCost) {
        newState = state;
        break;
      }
      const currentLevel = state.mentorLevels[mentorKey] || 1;
      const upgradeCost = GameService.calculateMentorUpgradeCost(mentor, currentLevel);
      if (state.seeds >= upgradeCost) {
        const newLevel = currentLevel + 1;
        const newMentorLevels = { ...state.mentorLevels, [mentorKey]: newLevel };
        const newSps = recalculateTotalMentorSps(state.ownedCharacters, newMentorLevels);
        newState = {
          ...state,
          seeds: state.seeds - upgradeCost,
          mentorLevels: newMentorLevels,
          sps: newSps,
        };
      } else {
        newState = state;
      }
      break;
    }

    case 'REFRESH_DATA': {
      const purchasedMentor = action.payload.mentor;
      if (purchasedMentor && !state.ownedCharacters.includes(purchasedMentor.key)) {
        const newOwnedCharacters = [...state.ownedCharacters, purchasedMentor.key];
        newState = { ...state, ownedCharacters: newOwnedCharacters };
        newState.sps = recalculateTotalMentorSps(newState.ownedCharacters, newState.mentorLevels);
      } else {
        newState = state;
      }
      break;
    }

    case 'START_FOCUS': {
      const metadata = action.payload.metadata || {};
      newState = { 
        ...state, 
        focus: { 
          ...state.focus, 
          status: 'active', 
          length: action.payload.length, 
          remaining: action.payload.length * 60,
          durationMs: action.payload.length * 60 * 1000,
          elapsed: 0,
          lastTick: Date.now(),
          sessionId: Date.now().toString(),
          intention: action.payload.intention || '',
          scene: action.payload.scene || 'dawn',
          artSeed: action.payload.artSeed || Date.now(),
          taskId: metadata.taskId || null,
          taskTitle: metadata.taskTitle || '',
          taskProject: metadata.taskProject || ''
        } 
      };
      break;
    }

    case 'ATTACH_FOCUS_METADATA': {
      if (
        !['active', 'paused'].includes(state.focus.status)
        || state.focus.taskId
        || (action.payload?.sessionId && action.payload.sessionId !== state.focus.sessionId)
      ) {
        newState = state;
        break;
      }

      newState = {
        ...state,
        focus: {
          ...state.focus,
          taskId: action.payload?.taskId || null,
          taskTitle: action.payload?.taskTitle || '',
          taskProject: action.payload?.taskProject || ''
        }
      };
      break;
    }

    case 'PAUSE_FOCUS': {
      if (state.focus.status !== 'active') {
        newState = state;
        break;
      }
      const now = Date.now();
      const delta = Math.max(0, now - (state.focus.lastTick || now));
      const durationMs = state.focus.durationMs || (state.focus.length * 60 * 1000);
      const newElapsed = Math.min(durationMs, (state.focus.elapsed || 0) + delta);
      
      if (newElapsed >= durationMs) {
        // We reached the deadline while pausing, complete the session instead.
        const sessionId = state.focus.sessionId || Date.now().toString();
        const alreadyCompleted = state.gallery?.some(art => art.id === sessionId);
        
        if (!alreadyCompleted) {
          const reward = GameService.calculateFocusReward(state.focus.length, state.compost);
          const todayDate = DateService.getDayKey();
          let newStreak = { ...state.streak };
          if (state.streak.lastDay === "" || DateService.isYesterday(state.streak.lastDay)) {
            newStreak.current = state.streak.current + 1;
          } else if (!DateService.isToday(state.streak.lastDay)) {
            newStreak.current = 1;
          }
          newStreak.best = Math.max(newStreak.best, newStreak.current);
          newStreak.lastDay = todayDate;

          const newArt = {
            id: sessionId,
            scene: state.focus.scene,
            artSeed: state.focus.artSeed,
            intention: state.focus.intention || '',
            length: state.focus.length,
            completedAt: Date.now(),
            reward,
            taskId: state.focus.taskId || null,
            taskTitle: state.focus.taskTitle || '',
            taskProject: state.focus.taskProject || ''
          };

          newState = {
            ...state,
            seeds: state.seeds + reward,
            focus: { ...state.focus, status: 'completed', remaining: 0, elapsed: durationMs, lastTick: now },
            streak: newStreak,
            daily: { ...state.daily, completedToday: true },
            gallery: [...(state.gallery || []), newArt]
          };
        } else {
           newState = { ...state, focus: { ...state.focus, status: 'completed', remaining: 0, elapsed: durationMs, lastTick: now } };
        }
      } else {
        newState = {
          ...state,
          focus: {
            ...state.focus,
            status: 'paused',
            elapsed: newElapsed,
            remaining: Math.max(0, Math.ceil((durationMs - newElapsed) / 1000)),
            lastTick: now
          }
        };
      }
      break;
    }

    case 'RESUME_FOCUS': {
      if (state.focus.status !== 'paused') {
        newState = state;
        break;
      }
      newState = {
        ...state,
        focus: {
          ...state.focus,
          status: 'active',
          lastTick: Date.now()
        }
      };
      break;
    }

    case 'STOP_FOCUS': {
      newState = { ...state, focus: { ...state.focus, status: 'idle', remaining: 0, elapsed: 0 } };
      break;
    }

    case 'FOCUS_TICK': {
      if (state.focus.status === 'active' && state.focus.remaining > 0) {
        const now = Date.now();
        const delta = Math.max(0, now - (state.focus.lastTick || now)); // Removed 5s cap for real elapsed tracking
        const durationMs = state.focus.durationMs || (state.focus.length * 60 * 1000);
        const newElapsed = Math.min(durationMs, (state.focus.elapsed || 0) + delta);
        
        const newRemaining = Math.max(0, Math.ceil((durationMs - newElapsed) / 1000));
        
        if (newRemaining === 0) {
          const sessionId = state.focus.sessionId || Date.now().toString();
          const alreadyCompleted = state.gallery?.some(art => art.id === sessionId);
          
          if (!alreadyCompleted) {
            const reward = GameService.calculateFocusReward(state.focus.length, state.compost);
            const todayDate = DateService.getDayKey();
            let newStreak = { ...state.streak };
            if (state.streak.lastDay === "" || DateService.isYesterday(state.streak.lastDay)) {
              newStreak.current = state.streak.current + 1;
            } else if (!DateService.isToday(state.streak.lastDay)) {
              newStreak.current = 1;
            }
            newStreak.best = Math.max(newStreak.best, newStreak.current);
            newStreak.lastDay = todayDate;

            const newArt = {
              id: sessionId,
              scene: state.focus.scene,
              artSeed: state.focus.artSeed,
              intention: state.focus.intention || '',
              length: state.focus.length,
              completedAt: Date.now(),
              reward,
              taskId: state.focus.taskId || null,
              taskTitle: state.focus.taskTitle || '',
              taskProject: state.focus.taskProject || ''
            };

            newState = {
              ...state,
              seeds: state.seeds + reward,
              focus: { ...state.focus, status: 'completed', remaining: 0, elapsed: durationMs, lastTick: now },
              streak: newStreak,
              daily: { ...state.daily, completedToday: true },
              gallery: [...(state.gallery || []), newArt]
            };
          } else {
             newState = { ...state, focus: { ...state.focus, status: 'completed', remaining: 0, elapsed: durationMs, lastTick: now } };
          }
        } else {
          newState = { ...state, focus: { ...state.focus, remaining: newRemaining, elapsed: newElapsed, lastTick: now } };
        }
      } else {
        newState = state;
      }
      break;
    }

    case 'CLAIM_DAILY': {
      const today = DateService.getDayKey();
      if (state.daily.completedToday && state.daily.lastClaimDay !== today) {
        const reward = state.daily.rewards[state.daily.index];
        const nextIndex = Math.min(state.daily.index + 1, state.daily.rewards.length - 1);
        newState = {
          ...state,
          seeds: state.seeds + reward,
          daily: { ...state.daily, index: nextIndex, lastClaimDay: today }
        };
      } else {
        newState = state;
      }
      break;
    }

    case 'WATER_GARDEN': {
      const { chargeLevel } = action.payload;
      const baseReward = 1000;
      const maxBoost = 999000;
      const randomFactor = 0.85 + Math.random() * 0.3; // ±15%
      const reward = Math.max(25, Math.floor((baseReward + maxBoost * chargeLevel) * randomFactor));
      newState = {
        ...state,
        seeds: state.seeds + reward,
        dailyWater: { ...state.dailyWater, used: true },
        lastWateringReward: reward,
      };
      break;
    }

    case 'START_PLOT': {
      const plotIndex = action.payload.index;
      const plot = state.plots[plotIndex];
      if (plot.status === 'ready') {
        const reward = GameService.calculateFocusReward(plot.minutes, state.compost);
        const cost = Math.floor(reward * 0.5);
        if (state.seeds >= cost) {
          const newPlots = [...state.plots];
          newPlots[plotIndex] = { ...plot, status: 'growing', startedAt: Date.now(), endAt: Date.now() + (plot.minutes * 60 * 1000), reward, cost };
          newState = { ...state, seeds: state.seeds - cost, plots: newPlots };
        } else {
          newState = state;
        }
      } else {
        newState = state;
      }
      break;
    }

    case 'UPDATE_PLOTS': {
      const now = Date.now();
      const newPlots = state.plots.map(plot => {
        if (plot.status === 'growing' && now >= plot.endAt) {
          return { ...plot, status: 'done' };
        }
        return plot;
      });
      newState = { ...state, plots: newPlots };
      break;
    }

    case 'CLAIM_PLOT': {
      const plotIndex = action.payload.index;
      const plot = state.plots[plotIndex];
      if (plot.status === 'done') {
        const newPlots = [...state.plots];
        newPlots[plotIndex] = { ...plot, status: 'ready', reward: 0, cost: 0, startedAt: 0, endAt: 0 };
        newState = { ...state, seeds: state.seeds + plot.reward, plots: newPlots };
      } else {
        newState = state;
      }
      break;
    }

    case 'ABORT_PLOT': {
      const plotIndex = action.payload.index;
      const plot = state.plots[plotIndex];
      if (plot.status === 'growing') {
        const newPlots = [...state.plots];
        newPlots[plotIndex] = { ...plot, status: 'ready', reward: 0, cost: 0, startedAt: 0, endAt: 0 };
        newState = { ...state, seeds: state.seeds + plot.cost, plots: newPlots }; // Refund cost
      } else {
        newState = state;
      }
      break;
    }

    case 'SET_PLOT_MINUTES': {
      const { index, minutes } = action.payload;
      const newPlots = [...state.plots];
      newPlots[index] = { ...newPlots[index], minutes };
      newState = { ...state, plots: newPlots };
      break;
    }

    case 'SET_FOCUS_LENGTH': {
      newState = { ...state, focus: { ...state.focus, length: action.payload } };
      break;
    }

    case 'UPDATE_PROFILE': {
      newState = { ...state, profile: { ...state.profile, ...action.payload } };
      break;
    }

    default:
      newState = state;
  }

  // Fallback for unchanged state
  if (!newState) {
    return state;
  }

  if (newState !== state) {
    console.log('✅ Reducer complete. Seeds changed from', state.seeds, 'to', newState.seeds);
  }

  return newState;
}